/**
 * O'Copro - App Principal
 * Gestion des routes avec support Admin/Syndic/Résident + Invitations
 * ✅ NOUVEAU: Gestion des abonnements avec bannière et écran de blocage
 * ✅ MISE À JOUR: Route /requests/labels pour la page dédiée gestion étiquettes BAL
 * ✅ MISE À JOUR: Intégration push notifications FCM (usePushNotifications)
 * ✅ NOUVEAU: ErrorBoundary global — capture les crashs React, affiche un fallback UI
 * ✅ NOUVEAU: Routes légales /terms et /privacy (CGU + Politique de confidentialité)
 * ✅ NOUVEAU: Splash Screen au démarrage avec logo O'Copro
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useAuthStore, useUIStore, useResidenceStore } from '@/stores';

// Components statiques — nécessaires au premier rendu
import { Spinner } from '@/components/ui';
import { Toast } from '@/components/ui/Toast';
import { ImpersonationBanner } from '@/components/layout/ImpersonationBanner';
import { OfflineBanner } from '@/components/layout/OfflineBanner';
import { SubscriptionBanner } from '@/components/shared/SubscriptionBanner';
import { PlanAlertBanner } from '@/components/shared/PlanAlertBanner';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { SplashScreen } from '@/components/ui/SplashScreen';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { usePendingBadge } from '@/hooks/usePendingBadge';
import { useInAppReview } from '@/hooks/useInAppReview';
import { useBiometricLock } from '@/hooks/useBiometricLock';
import { useChottuLink } from '@/hooks/useChottuLink';
import { BiometricLockScreen } from '@/components/ui/BiometricLockScreen';
import { Menu } from '@/components/layout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { SyndicLayout } from '@/components/layout/SyndicLayout';

// ── Pages lazy-loadées (chargées à la demande) ────────────────────────────────

// Auth
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const EmailVerificationPage = lazy(() => import('@/pages/auth/EmailVerificationPage'));
const VerifyRedirectPage = lazy(() => import('@/pages/auth/VerifyRedirectPage'));
const ForceChangePasswordPage = lazy(() => import('@/pages/auth/ForceChangePasswordPage'));

// Legal
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/PrivacyPolicyPage'));

// Onboarding
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));

// Résident
const HomePage = lazy(() => import('@/pages/home/HomePage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const NotificationSettingsPage = lazy(() => import('@/pages/profile/NotificationSettingsPage'));
const InfoPage = lazy(() => import('@/pages/info/InfoPage'));
const CalendarPage = lazy(() => import('@/pages/calendar/CalendarPage'));
const NewEventPage = lazy(() => import('@/pages/calendar/NewEventPage'));
const RequestsListPage = lazy(() => import('@/pages/requests/RequestsListPage'));
const NewRequestPage = lazy(() => import('@/pages/requests/NewRequestPage'));
const RequestDetailPage = lazy(() => import('@/pages/requests/RequestDetailPage'));
const LabelPrintPage = lazy(() => import('@/pages/requests/LabelPrintPage'));
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage'));
const ManageDocumentsPage = lazy(() => import('@/pages/documents/ManageDocumentsPage'));
const PollsViewPage = lazy(() => import('@/pages/polls/PollsViewPage'));
const ManagePollsPage = lazy(() => import('@/pages/polls/ManagePollsPage'));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));
const SendNotificationPage = lazy(() => import('@/pages/notifications/SendNotificationPage'));
const ChatListPage = lazy(() => import('@/pages/chat/ChatGroupsPage'));
const ChatConversationPage = lazy(() => import('@/pages/chat/ChatConversationPage'));
const ChatAdminPage = lazy(() => import('@/pages/chat/ChatAdminPage'));
const ValidationsPage = lazy(() => import('@/pages/validations/ValidationsPage'));
const ValidationCampaignPage = lazy(() => import('@/pages/validations/ValidationCampaignPage'));
const InscriptionsPage = lazy(() => import('@/pages/inscriptions/InscriptionsPage'));
const ManageUsersPage = lazy(() => import('@/pages/users/ManageUsersPage'));
const PendingUsersPage = lazy(() => import('@/pages/users/PendingUsersPage'));
const InvitePage = lazy(() => import('@/pages/users/InvitePage'));
const PendingStatusPage = lazy(() => import('@/pages/users/PendingStatusPage'));
const AccountDisabledPage = lazy(() => import('@/pages/users/AccountDisabledPage'));
const RejectedPage = lazy(() => import('@/pages/users/RejectedPage'));
const ConfirmNewcomerPage = lazy(() => import('@/pages/users/ConfirmNewcomerPage'));
const BecomeCouncilPage = lazy(() => import('@/pages/users/BecomeCouncilPage'));
const ResidenceSettingsPage = lazy(() => import('@/pages/settings/ResidenceSettingsPage'));
const BailleursPage = lazy(() => import('@/pages/settings/BailleursPage'));
const ServicesPage = lazy(() => import('@/pages/settings/ServicesPage'));
const CSGuidePage = lazy(() => import('@/pages/gestion/CSGuidePage'));
const BudgetBenchmarkPage = lazy(() => import('@/pages/gestion/BudgetBenchmarkPage'));
const GardienManagementPage = lazy(() => import('@/pages/gestion/GardienManagementPage'));
const CSSyndicManagementPage = lazy(() => import('@/pages/gestion/CSSyndicManagementPage'));
const SupportPage = lazy(() => import('@/pages/support/SupportPage'));
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));
const SubscriptionExpiredPage = lazy(() => import('@/pages/errors/SubscriptionExpiredPage'));
const MonitoringPage = lazy(() => import('@/pages/admin/MonitoringPage'));

// Admin
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminResidencesPage = lazy(() => import('@/pages/admin/AdminResidencesPage'));
const AdminResidenceDetailPage = lazy(() => import('@/pages/admin/AdminResidenceDetailPage'));
const AdminCreateResidencePage = lazy(() => import('@/pages/admin/AdminCreateResidencePage'));
const AdminEditResidencePage = lazy(() => import('@/pages/admin/AdminEditResidencePage'));
const AdminBudgetBenchmarkPage = lazy(() => import('@/pages/admin/AdminBudgetBenchmarkPage'));
const AdminSyndicsPage = lazy(() => import('@/pages/admin/AdminSyndicsPage'));
const AdminSyndicDetailPage = lazy(() => import('@/pages/admin/AdminSyndicDetailPage'));
const AdminCreateSyndicPage = lazy(() => import('@/pages/admin/AdminCreateSyndicPage'));
const AdminEditSyndicPage = lazy(() => import('@/pages/admin/AdminEditSyndicPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'));
const AdminUserDetailPage = lazy(() => import('@/pages/admin/AdminUserDetailPage'));
const AdminTransferResidencePage = lazy(() => import('@/pages/admin/AdminTransferResidencePage'));
const SubscriptionManagementPage = lazy(() => import('@/pages/admin/SubscriptionManagementPage'));
const AdminCompliancePage = lazy(() => import('@/pages/admin/AdminCompliancePage'));

// Syndic
const SyndicDashboardPage = lazy(() => import('@/pages/syndic/SyndicDashboardPage'));
const SyndicResidencesPage = lazy(() => import('@/pages/syndic/SyndicResidencesPage'));
const SyndicResidenceDetailPage = lazy(() => import('@/pages/syndic/SyndicResidenceDetailPage'));
const SyndicRequestsPage = lazy(() => import('@/pages/syndic/SyndicRequestsPage'));
const SyndicRequestDetailPage = lazy(() => import('@/pages/syndic/SyndicRequestDetailPage'));
const SyndicDocumentsPage = lazy(() => import('@/pages/syndic/SyndicDocumentsPage'));
const SyndicCalendarPage = lazy(() => import('@/pages/syndic/SyndicCalendarPage'));
const SyndicProfilePage = lazy(() => import('@/pages/syndic/SyndicProfilePage'));

// ============================================================================
// ROUTE GUARDS
// ============================================================================

/**
 * Route protégée - nécessite auth + résidence + statut validé
 * Exception pour admin/syndic qui n'ont pas besoin de résidence
 * ✅ Vérifie aussi l'état de l'abonnement
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, currentUser, impersonation } = useAuthStore();
  const currentResidence = useResidenceStore(state => state.currentResidence);
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ✅ En mode impersonation, utiliser l'utilisateur cible
  const effectiveUser = impersonation.isActive ? impersonation.targetUser : currentUser;

  // Admin/Syndic n'ont pas besoin de résidence
  if (effectiveUser && !effectiveUser.residenceId && !['admin', 'syndic'].includes(effectiveUser.category)) {
    return <Navigate to="/onboarding" replace />;
  }

  // Statut pending (sauf admin/syndic)
  if (effectiveUser?.status === 'pending' && !['admin', 'syndic'].includes(effectiveUser.category)) {
    return <Navigate to="/pending-status" replace />;
  }

  if (effectiveUser?.status === 'rejected' && !['admin', 'syndic'].includes(effectiveUser.category || '')) {
    return <Navigate to="/rejected" replace />;
  }

  // Compte désactivé (sauf admin)
  if (effectiveUser?.enabled === false && effectiveUser.category !== 'admin') {
    return <Navigate to="/account-disabled" replace />;
  }

  // Mot de passe temporaire — forcer le changement avant tout accès
  if (effectiveUser?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // ✅ Vérification de l'abonnement (sauf admin/syndic et page subscription-expired)
  if (
    currentResidence?.subscription &&
    !['admin', 'syndic'].includes(effectiveUser?.category || '') &&
    location.pathname !== '/subscription-expired'
  ) {
    const endDate = new Date(currentResidence.subscription.endDate);
    const now = new Date();
    const gracePeriodDays = 7;
    const daysSinceExpiry = Math.ceil((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Bloquer si : statut Firestore 'expired' OU plus de 7 jours depuis expiration
    if (currentResidence.subscription.status === 'expired' || daysSinceExpiry > gracePeriodDays) {
      return <Navigate to="/subscription-expired" replace />;
    }
  }

  return <>{children}</>;
};

/**
 * Route Admin - nécessite category === 'admin'
 */
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, currentUser, impersonation } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // En mode impersonation, l'admin peut toujours accéder aux routes admin
  const effectiveCategory = impersonation.isActive 
    ? impersonation.originalUser?.category 
    : currentUser?.category;

  if (effectiveCategory !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Route Syndic - nécessite category === 'syndic'
 */
const SyndicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, currentUser } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser?.category !== 'syndic') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Route d'onboarding
 */
const OnboardingRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, currentUser, impersonation } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/register" replace />;
  }

  // ✅ En mode impersonation, utiliser l'utilisateur cible
  const effectiveUser = impersonation.isActive ? impersonation.targetUser : currentUser;

  // Admin/Syndic n'ont pas d'onboarding résidence
  if (effectiveUser?.category === 'admin' && !impersonation.isActive) {
    return <Navigate to="/admin" replace />;
  }
  if (effectiveUser?.category === 'syndic') {
    return <Navigate to="/syndic" replace />;
  }

  if (effectiveUser?.residenceId) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Route pour utilisateurs en attente
 */
const PendingStatusRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, currentUser, impersonation } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ✅ En mode impersonation, utiliser l'utilisateur cible
  const effectiveUser = impersonation.isActive ? impersonation.targetUser : currentUser;

  if (!effectiveUser?.residenceId && !['admin', 'syndic'].includes(effectiveUser?.category || '')) {
    return <Navigate to="/onboarding" replace />;
  }

  if (effectiveUser?.status !== 'pending') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Route publique
 */
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  return <>{children}</>;
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

/**
 * Wrapper pour afficher la bannière d'abonnement uniquement pour les résidents
 */
const SubscriptionBannerWrapper = () => {
  const { currentUser, impersonation } = useAuthStore();
  const effectiveUser = impersonation.isActive ? impersonation.targetUser : currentUser;
  
  // Ne pas afficher pour admin/syndic
  if (['admin', 'syndic'].includes(effectiveUser?.category || '')) {
    return null;
  }
  
  return <SubscriptionBanner />;
};

function App() {
  const { setFirebaseUser, currentUser, isAuthenticated, isLoading, impersonation } = useAuthStore();
  const { toasts, hideToast, isMenuOpen, closeMenu } = useUIStore();
  const [showSplash, setShowSplash] = useState(true);

  // ✅ NOUVEAU: Initialisation push notifications FCM
  // Le hook gère l'enregistrement du token, l'écoute foreground et le cleanup
  usePushNotifications();
  useVersionCheck();
  usePendingBadge();
  useInAppReview();
  useChottuLink();
  const { isLocked, isAuthenticating, authenticate } = useBiometricLock(isAuthenticated);

  // Écouter les changements d'authentification Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
    });

    return () => unsubscribe();
  }, [setFirebaseUser]);

  // ✅ Redirection automatique selon la catégorie après login
  // SAUF en mode impersonation où on reste sur les routes résidents
  const location = useLocation();
  useEffect(() => {
    // ✅ Ne pas rediriger si impersonation active
    if (impersonation.isActive) {
      return;
    }

    if (isAuthenticated && currentUser && location.pathname === '/') {
      if (currentUser.category === 'admin') {
        window.location.href = '/admin';
      } else if (currentUser.category === 'syndic') {
        window.location.href = '/syndic';
      }
    }
  }, [isAuthenticated, currentUser, location.pathname, impersonation.isActive]);

  // React Router ne réinitialise pas le scroll entre les routes (contrairement à une
  // navigation classique). Sans ça, une page qui s'ouvre alors que le body est encore
  // scrollé (ex: on scrollait l'accueil avant de naviguer) affiche son contenu du milieu
  // en premier, et son header `sticky` — déjà "collé" au chargement — se cale au bord
  // physique du viewport en ignorant le padding safe-area du body, passant sous la
  // barre de statut/l'encoche.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Verrouillage biométrique */}
      {isLocked && (
        <BiometricLockScreen isAuthenticating={isAuthenticating} onRetry={authenticate} />
      )}

      {/* ✅ Splash Screen — affiché au démarrage pendant l'init Firebase Auth */}
      {showSplash && (
        <SplashScreen
          isReady={!isLoading}
          onFinished={() => setShowSplash(false)}
          minDuration={1800}
        />
      )}

      {/* ✅ Bandeau d'impersonation global */}
      <ImpersonationBanner />

      {/* ✅ Spacer quand le bandeau est affiché */}
      {impersonation.isActive && <div className="h-10" />}

      {/* ✅ Bandeau d'avertissement abonnement */}
      <SubscriptionBannerWrapper />

      {/* Bandeau conformité plan (CS uniquement) */}
      <PlanAlertBanner />

      {/* ✅ Bandeau hors-connexion */}
      <OfflineBanner />

      {/* Menu latéral (pour résidents) */}
      <Menu isOpen={isMenuOpen} onClose={closeMenu} />

      {/* ✅ ErrorBoundary — capture les crashs React et affiche un fallback UI */}
      <ErrorBoundary>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Spinner size="lg" />
        </div>
      }>
      {/* Routes */}
      <Routes>
        {/* ============================================================ */}
        {/* ROUTES PUBLIQUES                                             */}
        {/* ============================================================ */}
        
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/verify-email" element={<PublicRoute><EmailVerificationPage /></PublicRoute>} />
        <Route path="/v/:code" element={<VerifyRedirectPage />} />
        <Route path="/change-password" element={<ForceChangePasswordPage />} />

        {/* ✅ NOUVEAU: Pages légales (accessibles sans authentification) */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        {/* Lien d'invitation */}
        <Route path="/join/:code" element={<OnboardingPage mode="invite" />} />

        {/* ============================================================ */}
        {/* ONBOARDING                                                   */}
        {/* ============================================================ */}
        
        <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />

        {/* ============================================================ */}
        {/* STATUTS SPÉCIAUX                                             */}
        {/* ============================================================ */}
        
        <Route path="/pending-status" element={<PendingStatusRoute><PendingStatusPage /></PendingStatusRoute>} />
        <Route path="/account-disabled" element={<AccountDisabledPage />} />
        <Route path="/rejected" element={<RejectedPage />} />

        {/* ============================================================ */}
        {/* ROUTES ADMIN - /admin/*                                      */}
        {/* ============================================================ */}
        
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminDashboardPage />} />
          {/* Résidences */}
          <Route path="residences" element={<AdminResidencesPage />} />
          <Route path="residences/new" element={<AdminCreateResidencePage />} />
          <Route path="residences/:id" element={<AdminResidenceDetailPage />} />
          <Route path="residences/:id/edit" element={<AdminEditResidencePage />} />
          <Route path="residences/:id/budget-benchmark" element={<AdminBudgetBenchmarkPage />} />
          {/* Syndics */}
          <Route path="syndics" element={<AdminSyndicsPage />} />
          <Route path="syndics/new" element={<AdminCreateSyndicPage />} />
          <Route path="syndics/:id" element={<AdminSyndicDetailPage />} />
          <Route path="syndics/:id/edit" element={<AdminEditSyndicPage />} />
          {/* Utilisateurs */}
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:id" element={<AdminUserDetailPage />} />
          {/* Transfert */}
          <Route path="transfer" element={<AdminTransferResidencePage />} />
          {/* ✅ Abonnements */}
          <Route path="subscriptions" element={<SubscriptionManagementPage />} />
          {/* Conformité plans */}
          <Route path="compliance" element={<AdminCompliancePage />} />
        </Route>

        {/* ============================================================ */}
        {/* ROUTES SYNDIC - /syndic/*                                    */}
        {/* ============================================================ */}
        
        <Route path="/syndic" element={<SyndicRoute><SyndicLayout /></SyndicRoute>}>
          <Route index element={<SyndicDashboardPage />} />
          {/* Résidences */}
          <Route path="residences" element={<SyndicResidencesPage />} />
          <Route path="residences/:id" element={<SyndicResidenceDetailPage />} />
          {/* Demandes */}
          <Route path="requests" element={<SyndicRequestsPage />} />
          <Route path="requests/:residenceId/:requestId" element={<SyndicRequestDetailPage />} />
          {/* Documents */}
          <Route path="documents" element={<SyndicDocumentsPage />} />
          {/* Calendrier */}
          <Route path="calendar" element={<SyndicCalendarPage />} />
          {/* Profil syndic */}
          <Route path="profile" element={<SyndicProfilePage />} />
        </Route>

        {/* ============================================================ */}
        {/* ROUTES RÉSIDENTS                                             */}
        {/* ============================================================ */}
        
        {/* Accueil */}
        <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        
        {/* Profil */}
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
        
        {/* Informations résidence */}
        <Route path="/info" element={<ProtectedRoute><InfoPage /></ProtectedRoute>} />
        
        {/* Calendrier */}
        <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/calendar/new" element={<ProtectedRoute><NewEventPage /></ProtectedRoute>} />
        
        {/* Demandes */}
        <Route path="/requests" element={<ProtectedRoute><RequestsListPage /></ProtectedRoute>} />
        <Route path="/requests/new/:type" element={<ProtectedRoute><NewRequestPage /></ProtectedRoute>} />
        <Route path="/requests/:id" element={<ProtectedRoute><RequestDetailPage /></ProtectedRoute>} />
        {/* ✅ NOUVEAU: Page dédiée gestion et impression étiquettes BAL (CS/Syndic/Admin) */}
        <Route path="/requests/labels" element={<ProtectedRoute><LabelPrintPage /></ProtectedRoute>} />
        
        {/* Documents */}
        <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
        <Route path="/manage-documents" element={<ProtectedRoute><ManageDocumentsPage /></ProtectedRoute>} />
        
        {/* Sondages */}
        <Route path="/polls" element={<ProtectedRoute><PollsViewPage /></ProtectedRoute>} />
        <Route path="/manage-polls" element={<ProtectedRoute><ManagePollsPage /></ProtectedRoute>} />
        
        {/* Notifications */}
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/send-notification" element={<ProtectedRoute><SendNotificationPage /></ProtectedRoute>} />
        
        {/* Chat */}
        <Route path="/chat" element={<ProtectedRoute><ChatListPage /></ProtectedRoute>} />
        <Route path="/chat/admin" element={<ProtectedRoute><ChatAdminPage /></ProtectedRoute>} />
        <Route path="/chat/:groupId" element={<ProtectedRoute><ChatConversationPage /></ProtectedRoute>} />
        
        {/* Validations */}
        <Route path="/validations" element={<ProtectedRoute><ValidationsPage /></ProtectedRoute>} />

        {/* Validation Campaign: */}
        <Route path="/validation-campaign" element={<ProtectedRoute><ValidationCampaignPage /></ProtectedRoute>} />
        
        {/* Inscriptions */}
        <Route path="/inscriptions" element={<ProtectedRoute><InscriptionsPage /></ProtectedRoute>} />
        
        {/* Gestion des utilisateurs */}
        <Route path="/users" element={<ProtectedRoute><ManageUsersPage /></ProtectedRoute>} />
        <Route path="/pending-users" element={<ProtectedRoute><PendingUsersPage /></ProtectedRoute>} />
        <Route path="/invite" element={<ProtectedRoute><InvitePage /></ProtectedRoute>} />
        
        {/* Devenir membre du conseil */}
        <Route path="/become-council" element={<ProtectedRoute><BecomeCouncilPage /></ProtectedRoute>} />
        <Route path="/confirm-newcomer" element={<ProtectedRoute><ConfirmNewcomerPage /></ProtectedRoute>} />
        
        {/* Paramètres résidence */}
        <Route path="/residence-settings" element={<ProtectedRoute><ResidenceSettingsPage /></ProtectedRoute>} />
        <Route path="/bailleurs" element={<ProtectedRoute><BailleursPage /></ProtectedRoute>} />
        <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
        
        {/* Guide du Conseil Syndical */}
        <Route path="/guide-cs" element={<ProtectedRoute><CSGuidePage /></ProtectedRoute>} />

        {/* Budget & Benchmark IA */}
        <Route path="/budget-benchmark" element={<ProtectedRoute><BudgetBenchmarkPage /></ProtectedRoute>} />

        {/* Gestion du compte Gardien */}
        <Route path="/gardien-management" element={<ProtectedRoute><GardienManagementPage /></ProtectedRoute>} />

        {/* Gestion du compte Syndic par le CS */}
        <Route path="/cs-syndic-management" element={<ProtectedRoute><CSSyndicManagementPage /></ProtectedRoute>} />
        
        {/* Monitoring (ancien) */}
        <Route path="/monitoring" element={<ProtectedRoute><MonitoringPage /></ProtectedRoute>} />
        
        {/* Support */}
        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
        
        {/* Abonnement expiré */}
        <Route path="/subscription-expired" element={<ProtectedRoute><SubscriptionExpiredPage /></ProtectedRoute>} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>

      {/* Toasts */}
      <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
